import * as React from 'react';
import { api } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle, Tooltip } from '@/components/ui';
import { BookOpen, Link as LinkIcon } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

interface SuggestedArticle {
  id: string;
  title: string;
  descriptionText?: string;
  views: number;
  folder?: { name: string };
}

interface SuggestedArticlesProps {
  ticketId: string;
  collapsible?: boolean;
  /** Render only the article list content (no Card/header) — used when embedded inside a SidebarRow. */
  bare?: boolean;
}

export function SuggestedArticles({ ticketId, collapsible = false, bare = false }: SuggestedArticlesProps) {
  const { t } = useI18n();
  const [articles, setArticles] = React.useState<SuggestedArticle[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [linked, setLinked] = React.useState<Set<string>>(new Set());
  const [open, setOpen] = React.useState(true);

  React.useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    api
      .get<SuggestedArticle[]>('/kb/suggestions', { params: { ticketId } })
      .then(setArticles)
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [ticketId]);

  async function handleLink(articleId: string) {
    try {
      await api.post(`/admin/kb/articles/${articleId}/tickets/${ticketId}`);
      setLinked((prev) => new Set([...prev, articleId]));
    } catch {
      // Already linked or error — silently ignore
    }
  }

  const listContent = loading ? (
    <p className="px-2 py-2 text-xs text-ink-subtle">{t('tp.common.loading')}</p>
  ) : articles.length === 0 ? (
    <p className="px-2 py-2 text-xs text-ink-subtle">{t('tp.articles.none')}</p>
  ) : (
    articles.map((article) => (
      <div key={article.id} className="group flex items-start gap-2 rounded p-2 hover:bg-surface-hover">
        <div className="flex-1 min-w-0">
          <a
            href={`/kb/${article.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-brand-700 hover:underline line-clamp-1"
          >
            {article.title}
          </a>
          {article.descriptionText && (
            <p className="mt-0.5 text-xs text-ink-subtle line-clamp-2">{article.descriptionText}</p>
          )}
          <p className="mt-0.5 text-xs text-ink-subtle">
            {article.folder?.name} · {t('tp.articles.views', { count: article.views })}
          </p>
        </div>
        <Tooltip label={linked.has(article.id) ? t('tp.articles.linkedAsSolution') : t('tp.articles.linkAsSolution')}>
          <button
            type="button"
            onClick={() => handleLink(article.id)}
            disabled={linked.has(article.id)}
            className="shrink-0 rounded p-1 text-ink-subtle opacity-0 transition hover:bg-brand-50 hover:text-brand-600 focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-50"
            aria-label={linked.has(article.id) ? t('tp.articles.isLinkedAria', { title: article.title }) : t('tp.articles.linkAria', { title: article.title })}
          >
            <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
    ))
  );

  if (bare) return <div className="space-y-1">{listContent}</div>;

  if (loading) return null; // Don't show anything while loading
  if (articles.length === 0) return null; // No suggestions, hide the widget

  return (
    <Card>
      <CardHeader className="pb-2">
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={collapsible ? open : true}
          onClick={() => collapsible && setOpen((value) => !value)}
        >
          <BookOpen className="h-4 w-4 text-brand-600" aria-hidden="true" />
          <CardTitle className="flex-1 text-sm">{t('tp.articles.title')}</CardTitle>
          {collapsible && <span aria-hidden="true" className="text-ink-subtle">{open ? '−' : '＋'}</span>}
        </button>
      </CardHeader>
      <CardContent className="space-y-2">
        {listContent}
      </CardContent>
    </Card>
  );
}
